<template>
    <div v-if="permissions != null && $getPermission(permissions,1).is_granted" class="mx-2 mb-24 sm:ml-24 mr-2">
        <section v-if="selectedModel" class="hidden float-right md:clear-both lg:mx-auto w-max grid grid-cols-2 text-center font-semibold cursor-pointer">
            <div class="p-1 px-4 rounded-l" :class="{'bg-button text-black':displayTab == 1, 'border border-indigo-300 text-button': displayTab == 2}" @click="dTab(1)">Metrics</div>
            <div class="p-1 px-4 rounded-r" :class="{'bg-button text-black':displayTab == 2, 'border border-indigo-300 text-button': displayTab == 1}" @click="dTab(2)">History metrics</div>
        </section>

        <section class="mt-2">
            <div class="w-full flex justify-between">
                <div class="flex gap-2">
                    <div class="whitespace-nowrap cursor-pointer mt-1 p-1 ml-1 px-5 rounded bg-button text-buttonText font-semibold w-max" @click="refresh()" :title="$t('refresh')"><font-awesome-icon icon="fas fa-redo" /> {{ $t('refresh') }}</div>
                    <div v-if="selectedModel" class="flex justify-between bg-tableBG dark:bg-dark.tableBG text-textOnBkg dark:text-dark.textOnBkg p-1 h-8 mt-1.5 rounded-full border border-sepBorder dark:border-dark.sepBorder text-textOnBkg dark:text-dark.textOnBkg text-lg block"> 
                        <font-awesome-icon icon="fas fa-angle-left" @click="switchToNextModel(-1)" class="px-2 cursor-pointer text-xl transition-all transform hover:scale-125 ease-in-out" /> 
                        <div class="block w-60 px-1 text-center truncate text-base"> {{selectedModelName}} </div>
                        <font-awesome-icon icon="fas fa-angle-right" @click="switchToNextModel(+1)" class="transition-all px-2 transform hover:scale-125 ease-in-out text-xl cursor-pointer" />
                    </div>
                </div>
                <div class="flex pr-2 text-textOnBkg dark:text-dark.textOnBkg">
                    <MazSelect
                        size="sm"
                        v-model="selectedModel"
                        class="mr-2"
                        :options="filteredModelsList"
                        label="Model"
                        search
                        v-slot="{ option }"
                        option-input-value-key="label"
                        >
                        <div class="flex items-center" v-if="option.picture" style="width: 100%; gap: 1rem">
                            <MazAvatar size="0.6rem" :src="option.picture" class="cover" cover />
                            <strong>
                                {{ option.label }}
                            </strong>
                        </div>
                        <div v-else class="flex items-center" style="width: 100%; gap: 1rem">
                            {{ option.label }}
                        </div>
                    </MazSelect>
                    <MazPicker
                        v-model="dRange"
                        size="sm"
                        pickerPosition="right"
                        :label="$t('select_period')"
                        :max-date="new Date().toISOString().split('T')[0]"
                        double
                    />
                </div>
            </div>
            
            <div class="text-xs m-1 text-gray-500 hidden sm:block dark:text-dark.textOnBkg">*{{ $t('data_fetching_notice') }}</div>
        </section>
        
        <metrics-table v-if="dateFrom < dateTo && ($moment(dateFrom).format('YYYY-MM-DD')) !== $moment().format('YYYY-MM-DD')" :refreshing="refreshing" period="daily" tableTitle="Daily stats" :selectedPlatform="selectedPlatform" :selectedModel="selectedModel" :permissions="permissions" :selectedGroup="selectedGroup" :selectedGroupN="selectedGroupN" :dateFrom="dateFrom" :dateTo="dateTo" />
        <metrics-table :refreshing="refreshing" period="week" tableTitle="Weekly stats" :selectedPlatform="selectedPlatform"  :selectedModel="selectedModel" :permissions="permissions" :selectedGroup="selectedGroup" :selectedGroupN="selectedGroupN" :dateFrom="dateFrom" :dateTo="dateTo" />
        <metrics-table :refreshing="refreshing" period="month" tableTitle="Monthly stats" :selectedPlatform="selectedPlatform" :selectedModel="selectedModel" :permissions="permissions" :selectedGroup="selectedGroup" :selectedGroupN="selectedGroupN" :dateFrom="dateFrom" :dateTo="dateTo" />
    </div>

    <div v-if="permissions != null && (!$getPermission(permissions,1).is_granted)" class="text-center mt-52 text-textOnBkg">{{ $t("no_permission") }}</div>
</template>

<script setup>
    import metricsTable from '@/components/metrics/table.vue'
</script>

<script>
    import { useAuthStore } from '@/stores/auth';
    import { get_models } from "@/api/api.js"
    export default {
        data() {
            return {
                displayTab:1,
                loadingTables:0,
                loaded:false,
                refreshing:false,
                selectedPlatform: null,
                selectedEmpl: null,
                selectedModel: null,
                modelsList: [],
                dRange: {start: null, end: null},
                selectedGroupN: null,
                selectedGroup: null,
                dateFrom: this.$moment().subtract(9, 'days').format('YYYY-MM-DD'),
                dateTo: this.$moment().format('YYYY-MM-DD'),
            }
        },
        computed: {
            authStore() {
                return useAuthStore();
            },
            myself() {
                return this.authStore.myself;
            },
            permissions() {
                return this.authStore.permissions;
            },
            filteredModelsList() {
                if (!this.modelsList) return [];
                let r = this.modelsList.map(({ avatar, free }) => ({
                    picture: avatar,
                    label: free.name,
                    value: free.id
                }));
                //add an empty option with label: All creators, value: null
                r.unshift({ label: this.$t('all_creators'), value: null });
                return r;
            },
            selectedModelName() {
                return this.filteredModelsList.find((b) => b.value === this.selectedModel)?.label;
            }
        },
        components() { metricsTable},
        watch: {
            selectedModel: function (n,o) {
                if (n) {
                    this.$emit('selectedModel', n);
                    this.$emit('selectedModelN', this.selectedModelName);
                }
            },
            dRange: function (n,o) {
                this.dateFrom = n.start ? n.start : this.dateFrom;
                this.dateTo = n.end ? n.end : this.dateTo;
            },
        },
        methods: {
            switchToNextModel(parameter) {
                //get the index from this.modelsList by this.selectedModel and move the index by one left or right based on the parameter
                let index = this.modelsList.findIndex((x) => x.free.id === this.selectedModel);
                if (index === -1) return;
                index += parameter;
                if (index < 0) index = this.modelsList.length - 1;
                if (index >= this.modelsList.length) index = 0;
                this.selectedModel = this.modelsList[index].free.id;
            },
            updateSelectedPlatform(platform) {
                this.selectedPlatform = platform;
            },
            updateSelectedEmpl(empl) {
                this.selectedEmpl = empl;
            },
            updateSelectedGroupN(groupN) {
                this.selectedGroupN = groupN;
            },
            updateSelectedGroup(group) {
                this.selectedGroup = group;
            },
            updateDateRange(dates) {
                this.dateFrom = dates.start ? dates.start : this.dateFrom;
                this.dateTo = dates.end ? dates.end : this.dateTo;
            },
            dTab(w) {
                this.displayTab = w;
            },
            refresh() {
                this.refreshing = true
                setTimeout(() => {
                    this.refreshing = false
                }, 200);
            },
        },
        async created() {
            this.modelsList = await get_models(this.$cookies.get('userToken'),this.$cookies.get('c_id'),1,null,this.selectedGroup);
            if (this.modelsList[0] && this.pageTitle == 'Transactions') {
                this.inputModelName = this.modelsList[0].free?.name + " | " + this.modelsList[0].platformName + " | " + this.modelsList[0].code;
                setTimeout(() => {
                    this.selectedModel = this.modelsList[0]?.free?.id;
                    this.$emit('selectedModel', this.modelsList[0]?.free?.id);
                }, 500);
            }
        },
        mounted() {
            setTimeout(() => {
                this.loadingTables = 1;
            }, 1000);
            setTimeout(() => {
                this.loadingTables = 2;
            }, 1000); 
        },
    }
</script>